import {
  C2S,
  MAX_INPUTS_PER_MESSAGE,
  PROTOCOL_VERSION,
  S2C,
  decodeServer,
  encodeClient,
} from '@stillsun/shared';
import type {
  ChatS2C,
  EventsMsg,
  IdentityMsg,
  InputCmd,
  InventoryMsg,
  KickMsg,
  LootMsg,
  PlayerJoinedMsg,
  PlayerLeftMsg,
  PongMsg,
  PartyStateMsg,
  ProgressMsg,
  QuestStateMsg,
  ScoreMsg,
  ServerMessage,
  SnapshotMsg,
  TransferMsg,
  WelcomeMsg,
} from '@stillsun/shared';

export type ConnState = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'kicked' | 'closed';

export interface NetEventMap {
  state: { state: ConnState; detail: string };
  welcome: WelcomeMsg;
  snapshot: SnapshotMsg;
  playerJoined: PlayerJoinedMsg;
  playerLeft: PlayerLeftMsg;
  pong: PongMsg;
  chat: ChatS2C;
  kick: KickMsg;
  events: EventsMsg;
  score: ScoreMsg;
  identity: IdentityMsg;
  loot: LootMsg;
  inventory: InventoryMsg;
  /** M3 progression mirror: xp/level/skill points/calling + live ability cooldowns. */
  progress: ProgressMsg;
  /** M5 Expedition: the current rung of the quest chain (and the breather between rungs). */
  questState: QuestStateMsg;
  /**
   * Seamless zone travel (PROTOCOL-V3 §3.10). The city and the zone are two rooms in one
   * process, so crossing an exit is a hand-off on the *same socket*: no reconnect, no load
   * screen, and a `Welcome` for the new room follows immediately behind it.
   */
  transfer: TransferMsg;
  /**
   * Who the local player is grouped with (PROTOCOL-V3 §3.11, added with Phase 2d). Sent on
   * every membership or leadership change and otherwise a few times a second for the vitals;
   * an empty member list is how a leave, a kick and a disband all arrive.
   */
  partyState: PartyStateMsg;
}

type Listener<T> = (payload: T) => void;

class Emitter<M> {
  private readonly map = new Map<keyof M, Set<Listener<unknown>>>();

  on<K extends keyof M>(key: K, fn: Listener<M[K]>): () => void {
    let set = this.map.get(key);
    if (!set) {
      set = new Set();
      this.map.set(key, set);
    }
    set.add(fn as Listener<unknown>);
    return () => {
      set?.delete(fn as Listener<unknown>);
    };
  }

  emit<K extends keyof M>(key: K, payload: M[K]): void {
    const set = this.map.get(key);
    if (!set) return;
    for (const fn of [...set]) {
      try {
        (fn as Listener<M[K]>)(payload);
      } catch (err) {
        console.error('[stillsun] listener threw', err);
      }
    }
  }
}

const BACKOFF_MS = [300, 700, 1500, 3000, 5000, 8000] as const;
const PING_INTERVAL_MS = 2000;
const MAX_NAME_LEN = 20;
const MAX_CHAT_LEN = 180;

const defaultUrl = (): string => {
  const fromEnv = import.meta.env.VITE_WS_URL;
  if (typeof fromEnv === 'string' && fromEnv.length > 0) return fromEnv;
  return 'ws://localhost:8080/ws';
};

/** What the client asks the server for when it opens the socket. */
export interface ConnectOptions {
  /** JWT from /api/auth/*; '' while offline. */
  token: string;
  /** 'raid' | 'expedition' | 'yard'. Client-local until the protocol carries it. */
  mode: string;
  mapId: string;
  /** Friend/squad code, or ''. */
  squad: string;
}

export class NetClient extends Emitter<NetEventMap> {
  readonly baseUrl: string;

  state: ConnState = 'idle';
  /** Smoothed round-trip time in ms; 0 until the first pong. */
  rtt = 0;
  /** performance.now() + offset ≈ server clock in ms. */
  serverTimeOffset = 0;

  private ws: WebSocket | null = null;
  private name = 'drifter';
  private token = '';
  private query = '';
  private attempt = 0;
  private closedByUs = false;
  private haveTimeBase = false;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(url: string = defaultUrl()) {
    super();
    this.baseUrl = url;
  }

  /**
   * The socket URL including the room hint. `mode`/`mapId` are not in the protocol yet
   * (see the M2 report): the query string is a hint a server that does not understand it
   * simply ignores, and the client never depends on it being honoured.
   */
  get url(): string {
    return this.query.length > 0 ? `${this.baseUrl}?${this.query}` : this.baseUrl;
  }

  get connected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /** Estimated current server clock, in the same ms units as Snapshot.serverTime. */
  serverNow(): number {
    return performance.now() + this.serverTimeOffset;
  }

  /** True once a Snapshot or Pong has seeded `serverTimeOffset`. */
  get haveClock(): boolean {
    return this.haveTimeBase;
  }

  connect(name: string, opts: Partial<ConnectOptions> = {}): void {
    this.name = name.slice(0, MAX_NAME_LEN);
    this.token = opts.token ?? '';
    const params = new URLSearchParams();
    if (opts.mode !== undefined && opts.mode.length > 0) params.set('mode', opts.mode);
    if (opts.mapId !== undefined && opts.mapId.length > 0) params.set('map', opts.mapId);
    if (opts.squad !== undefined && opts.squad.length > 0) params.set('squad', opts.squad);
    this.query = params.toString();
    this.closedByUs = false;
    this.attempt = 0;
    this.open();
  }

  disconnect(): void {
    this.closedByUs = true;
    this.clearTimers();
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onopen = ws.onclose = ws.onerror = ws.onmessage = null;
      try {
        ws.close();
      } catch {
        /* ignore */
      }
    }
    this.setState('closed', '');
  }

  private setState(state: ConnState, detail: string): void {
    this.state = state;
    this.emit('state', { state, detail });
  }

  private clearTimers(): void {
    if (this.pingTimer !== null) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private open(): void {
    this.clearTimers();
    this.setState(this.attempt === 0 ? 'connecting' : 'reconnecting', '');

    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url);
    } catch (err) {
      console.warn('[stillsun] WebSocket construction failed', err);
      this.scheduleReconnect();
      return;
    }
    ws.binaryType = 'arraybuffer';
    this.ws = ws;

    ws.onopen = () => {
      this.attempt = 0;
      this.setState('open', '');
      this.sendHello();
      this.pingTimer = setInterval(() => {
        this.sendPing();
      }, PING_INTERVAL_MS);
      this.sendPing();
    };

    ws.onerror = () => {
      // 'close' always follows; nothing to do but avoid an unhandled error event.
    };

    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.clearTimers();
      if (this.closedByUs || this.state === 'kicked') return;
      console.info(`[stillsun] socket closed (${ev.code})`);
      this.scheduleReconnect();
    };

    ws.onmessage = (ev) => {
      const data: unknown = ev.data;
      if (!(data instanceof ArrayBuffer)) return;
      let msg: ServerMessage | null = null;
      try {
        msg = decodeServer(data);
      } catch (err) {
        console.warn('[stillsun] failed to decode server message', err);
        return;
      }
      if (msg) this.dispatch(msg);
    };
  }

  private scheduleReconnect(): void {
    const delay = BACKOFF_MS[Math.min(this.attempt, BACKOFF_MS.length - 1)] ?? 8000;
    this.attempt += 1;
    this.setState('reconnecting', `retrying in ${Math.round(delay / 1000)}s`);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.closedByUs) this.open();
    }, delay);
  }

  private dispatch(msg: ServerMessage): void {
    switch (msg.type) {
      case S2C.Welcome:
        this.emit('welcome', msg);
        break;
      case S2C.Snapshot:
        // Seed the clock immediately so remote interpolation is not frozen for
        // the first couple of seconds waiting on a pong.
        if (!this.haveTimeBase) {
          this.serverTimeOffset = msg.serverTime - performance.now();
          this.haveTimeBase = true;
        }
        this.emit('snapshot', msg);
        break;
      case S2C.PlayerJoined:
        this.emit('playerJoined', msg);
        break;
      case S2C.PlayerLeft:
        this.emit('playerLeft', msg);
        break;
      case S2C.Pong: {
        const now = performance.now();
        const sample = Math.max(0, now - msg.t);
        this.rtt = this.rtt === 0 ? sample : this.rtt * 0.8 + sample * 0.2;
        const offset = msg.serverTime + sample / 2 - now;
        this.serverTimeOffset = this.haveTimeBase
          ? this.serverTimeOffset * 0.9 + offset * 0.1
          : offset;
        this.haveTimeBase = true;
        this.emit('pong', msg);
        break;
      }
      case S2C.Chat:
        this.emit('chat', msg);
        break;
      case S2C.Events:
        this.emit('events', msg);
        break;
      case S2C.Score:
        this.emit('score', msg);
        break;
      case S2C.Identity:
        this.emit('identity', msg);
        break;
      case S2C.Loot:
        this.emit('loot', msg);
        break;
      case S2C.Inventory:
        this.emit('inventory', msg);
        break;
      case S2C.Progress:
        this.emit('progress', msg);
        break;
      case S2C.QuestState:
        this.emit('questState', msg);
        break;
      case S2C.Transfer:
        this.emit('transfer', msg);
        break;
      case S2C.PartyState:
        this.emit('partyState', msg);
        break;
      case S2C.Kick:
        this.closedByUs = true;
        this.clearTimers();
        this.setState('kicked', msg.reason);
        this.emit('kick', msg);
        try {
          this.ws?.close();
        } catch {
          /* ignore */
        }
        this.ws = null;
        break;
      default:
        break;
    }
  }

  // ---------------------------------------------------------------- outbound

  private raw(bytes: Uint8Array): void {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      // Copy into a plain ArrayBuffer: encodeClient returns Uint8Array<ArrayBufferLike>,
      // which WebSocket.send() will not accept under strict lib typings.
      const buf = new ArrayBuffer(bytes.byteLength);
      new Uint8Array(buf).set(bytes);
      ws.send(buf);
    } catch (err) {
      console.warn('[stillsun] send failed', err);
    }
  }

  sendHello(): void {
    try {
      this.raw(
        encodeClient({
          type: C2S.Hello,
          protocol: PROTOCOL_VERSION,
          name: this.name.slice(0, MAX_NAME_LEN),
          token: this.token,
        }),
      );
    } catch (err) {
      console.warn('[stillsun] hello encode failed', err);
    }
  }

  /** Chunks to MAX_INPUTS_PER_MESSAGE — the wire format writes the count as a u8. */
  sendInputs(cmds: readonly InputCmd[]): void {
    if (cmds.length === 0 || !this.connected) return;
    for (let i = 0; i < cmds.length; i += MAX_INPUTS_PER_MESSAGE) {
      const chunk = cmds.slice(i, i + MAX_INPUTS_PER_MESSAGE);
      try {
        this.raw(encodeClient({ type: C2S.Input, cmds: chunk }));
      } catch (err) {
        console.warn('[stillsun] input encode failed', err);
        return;
      }
    }
  }

  sendPing(): void {
    try {
      this.raw(encodeClient({ type: C2S.Ping, t: performance.now() }));
    } catch (err) {
      console.warn('[stillsun] ping encode failed', err);
    }
  }

  /**
   * Container/loot/inventory verbs. `targetId` is a container id (map data) or an entity
   * id (loot bag, >= 40000); `itemUid` is a stack uid for Take/Use/Drop.
   */
  sendInteract(action: number, targetId = 0, itemUid = 0): void {
    try {
      this.raw(
        encodeClient({
          type: C2S.Interact,
          action,
          targetId: targetId & 0xffff,
          itemUid: itemUid >>> 0,
        }),
      );
    } catch (err) {
      console.warn('[stillsun] interact encode failed', err);
    }
  }

  sendChat(text: string): void {
    const clean = text.trim().slice(0, MAX_CHAT_LEN);
    if (clean.length === 0) return;
    try {
      this.raw(encodeClient({ type: C2S.Chat, text: clean }));
    } catch (err) {
      console.warn('[stillsun] chat encode failed', err);
    }
  }
}
